import { css } from '@emotion/react'
import type { CalenderDateStr } from '@mathflat/shared/@common/types/string'
import { CalendarDate } from '@mathflat/shared/@modules/(dto)/CalendarDate.ts'
import { endOfMonth, format, startOfMonth } from 'date-fns'
import { ko } from 'date-fns/locale'
import { observer } from 'mobx-react'
import { useCallback, useMemo, useState } from 'react'
import SimpleBar from 'simplebar-react'

import type { AttendanceEntry } from '~/@common/api/parentApi'
import { useStudentAttendances } from '~/@common/hooks/useStudentAttendances'
import { commonRepo } from '~/@common/services/repo.service'
import { colors, typo } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import Calendar, { datePickerType } from '~/@common/ui/Calendar/Calendar'
import { PortalRootLayoutHeader } from '~/@common/ui/PortalRootLayoutHeader'
import GlobalHeader from '~/@pages/@widgets/(Navigation)/GlobalHeader/GlobalHeader'

const AttendanceHistoryPage = observer(() => {
  const [selectedDate, setSelectedDate] = useState(() => new CalendarDate())
  // 캘린더에 표시 중인 월 (월 이동 시 변경)
  const [viewingDate, setViewingDate] = useState(() => new Date())

  const studentId = commonRepo.studentId
  const startDate = format(startOfMonth(viewingDate), 'yyyy-MM-dd')
  const endDate = format(endOfMonth(viewingDate), 'yyyy-MM-dd')

  const { data: attendances } = useStudentAttendances(studentId, startDate, endDate)

  // 출석이 있는 날짜 Set (캘린더 specialDates용)
  const specialDates = useMemo(() => {
    if (!attendances) return new Set<CalenderDateStr>()
    return new Set(attendances.filter((a) => a.attendanceList.length > 0).map((a) => a.date))
  }, [attendances])

  // 선택된 날짜의 출석 기록
  const selectedDateAttendances = useMemo(() => {
    if (!attendances) return []
    const dateStr = format(selectedDate.dateObj, 'yyyy-MM-dd')
    return attendances.find((a) => a.date === dateStr)?.attendanceList ?? []
  }, [attendances, selectedDate])

  // 날짜 선택 핸들러
  const handleDateSelect = useCallback((date: CalendarDate) => {
    setSelectedDate(date)
  }, [])

  // 월 변경 핸들러
  const handleMonthChange = useCallback((startDateOfMonth: CalendarDate) => {
    setViewingDate(startDateOfMonth.dateObj)
  }, [])

  return (
    <>
      <PortalRootLayoutHeader>
        <GlobalHeader SlotCenter="지난 출석 보기" />
      </PortalRootLayoutHeader>

      <div css={pageStyle}>
        {/* 캘린더 영역 */}
        <div className="calendar-section">
          <Calendar
            css={calendarStyle}
            type={datePickerType}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            onPreviousClick={handleMonthChange}
            onNextClick={handleMonthChange}
            specialDates={specialDates}
          />
        </div>

        {/* 출석 상세 영역 */}
        <div css={detailSectionStyle}>
          {selectedDateAttendances.length > 0 ? (
            <SimpleBar style={{ maxHeight: '100%', height: '100%' }}>
              <div className="attendance">
                {/* 날짜 표시 */}
                <div className="date-label">
                  <strong>{format(selectedDate.dateObj, 'dd')}</strong>
                  <span>{format(selectedDate.dateObj, 'EEEE', { locale: ko })}</span>
                </div>

                {/* 출석 기록 리스트 */}
                <div className="record-list">
                  {selectedDateAttendances.map((record) => (
                    <AttendanceEntryRow key={record.id} record={record} />
                  ))}
                </div>
              </div>
            </SimpleBar>
          ) : (
            <div className="empty">출석 기록이 없습니다.</div>
          )}
        </div>
      </div>
    </>
  )
})

export default AttendanceHistoryPage

// ── 출석 기록 행 컴포넌트 ──

type AttendanceEntryRowProps = {
  record: AttendanceEntry
}

const AttendanceEntryRow = ({ record }: AttendanceEntryRowProps) => {
  // 시간 포맷: "오후 07:00"
  const enterTime = format(new Date(record.enterDatetime), 'aaa hh:mm', { locale: ko })
  const quitTime = record.quitDatetime
    ? format(new Date(record.quitDatetime), 'aaa hh:mm', { locale: ko })
    : null

  return (
    <div css={recordRowStyle}>
      {/* 등원 */}
      <div className="time-block">
        <div className="time-row">
          <span className="dot enter" />
          <span className="time enter-time">{enterTime}</span>
        </div>
        <span className="label">등원</span>
      </div>

      {/* 하원이 있는 경우에만 점선 + 하원 시간 표시 */}
      {quitTime && (
        <>
          <div className="divider" />
          <div className="time-block">
            <div className="time-row">
              <span className="dot quit" />
              <span className="time quit-time">{quitTime}</span>
            </div>
            <span className="label">하원</span>
          </div>
        </>
      )}
    </div>
  )
}

// ── 스타일 ──

// 페이지 전체 레이아웃
const pageStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  height: 100%;
  background: var(--Colors-Gray-50);

  ${mediaQuery.underTablet} {
    gap: 0;
  }

  /* 캘린더 영역 */
  .calendar-section {
    width: 400px;
    padding-top: 48px;

    ${mediaQuery.underTablet} {
      width: 100%;
      max-width: 400px;
      padding: 20px 24px;
    }
  }

  .empty {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: var(--Colors-Gray-600);
    ${typo.body02};
  }

  /* 출석 상세 레이아웃 - 데스크톱: 가로, 모바일: 세로 */
  .attendance {
    display: flex;
    gap: 16px;
    max-width: 460px;
    margin: 0 auto;

    ${mediaQuery.underTablet} {
      flex-direction: column;
      gap: 16px;
      padding: 0 40px;
    }
  }

  /* 날짜 라벨 (03 수요일) */
  .date-label {
    display: flex;
    flex-direction: column;
    width: 44px;
    text-align: center;

    > strong {
      color: var(--Colors-Gray-900);
      ${typo.heading03};
    }
    > span {
      color: var(--Colors-Gray-700);
      ${typo.caption02};
    }
  }

  /* 출석 기록 리스트 */
  .record-list {
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex: 1;
  }
`

// 출석 상세 영역 (SimpleBar 스크롤)
const detailSectionStyle = css`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  width: 100%;
  background: ${colors.white};
  padding: 24px 0;
`

// 캘린더 커스텀 스타일 (기본 Calendar CSS 오버라이드)
const calendarStyle = css`
  max-width: 100%;
  max-height: none;
  padding: 0;

  /* 셀이 넓어졌으므로 날짜 버튼을 중앙 정렬 */
  .ms__Calendar_dateGrid {
    justify-items: center;
    align-items: center;
  }

  /* 날짜 셀 오버라이드 */
  .ms__Calendar_date {
    font-weight: 700;
    &[data-special='true']:after {
      display: none;
    }
    &[data-special='true']:not([data-selected='true']) {
      width: 32px;
      height: 32px;
      border-radius: var(--Radius-Circle);
      background-color: var(--Colors-Gray-300);
    }
  }
`

// 출석 기록 행 (등원 → 점선 → 하원)
const recordRowStyle = css`
  display: flex;
  gap: 10px;
  align-items: center;

  /* 시간 블록 (등원/하원 각각) */
  .time-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    width: 97px;
  }

  .time-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  /* 색상 점 */
  .dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;

    &.enter {
      background: var(--Colors-Blue-600);
    }
    &.quit {
      background: var(--Colors-Gray-900);
    }
  }

  /* 시간 텍스트 */
  .time {
    font-weight: bold;
    ${typo.body01};
  }
  .enter-time {
    color: var(--Colors-Blue-600);
  }
  .quit-time {
    color: var(--Colors-Gray-900);
  }

  /* 등원/하원 라벨 */
  .label {
    color: var(--Colors-Gray-700);
    ${typo.body02};
  }

  /* 점선 연결선 */
  .divider {
    width: 189px;
    border-top: 1px dashed var(--Colors-Blue-600);

    ${mediaQuery.underDesktop} {
      width: auto;
      flex: 1;
    }
  }
`
